import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Tooltip } from '@mui/material';
import InsertCommentIcon from '@mui/icons-material/InsertComment';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import { creators as PeopleActions } from '../../../Reducers/Reporting/People';
import YearSelector from '../../../Common/Selectors/YearSelector';
import { financialMonth, makeDimDate, getMonthForPoeplePage, printFormat } from '../../../Utils/Functions';
import appdata from '../../../Services/appdata';
import TopChart from './TopChart';
import BottomChart from './BottomChart';

const People = ({
  defaultYear,
  defaultMonth,
  defaultDimDate,
  defaultStartMonth,
  selectedCompany,
  bubbleFlag,
}) => {
  const [selectedYears, setSelectedYears] = useState([defaultYear]);
  const [label, setLabel] = useState(`${new Date().getFullYear().toString()}`);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [selectedTopItems, setSelectedTopItems] = useState([]);
  const [selectedChartDimDate, setSelectedChartDimDate] = useState(defaultDimDate);
  const [resize, setResize] = useState(false);
  const lastDispatchedRef = useRef({ company: null, dimDate: null });

  const dispatch = useDispatch();
  const peopleSummary = useSelector((state) => state.Reporting.Summary.reportingSummaries?.people ?? '');
  const summaryData = useSelector((state) => state.Reporting.People.summaryData);
  const detailData = useSelector((state) => state.Reporting.People.detailData);
  const lastFetchedCompany = useSelector(
    (state) => state?.Reporting?.Default?.defaultCompany ?? null,
  );
  const lastFetchedDimDate = useSelector(
    (state) => state?.Reporting?.Default?.defaultDimDate ?? null,
  );

  const onResize = useCallback(() => {
    setResize((prev) => !prev);
  }, []);

  const handleFilter = useCallback((event) => {
    if (event.selectedYears) setSelectedYears(event.selectedYears);
    if (event.label) setLabel(event.label);
    if (event.selectedMonths) setSelectedMonths(event.selectedMonths);
    if (event.selectedTopItems) {
      setSelectedTopItems(event.selectedTopItems);
      appdata.setLocalItem('peoSelectedTopItems', event.selectedTopItems);

      if (event.selectedTopItems.length > 0) {
        const firstItem = event.selectedTopItems[0];
        const dimDate = printFormat.dateYYYYMMDD(
          getMonthForPoeplePage(firstItem.month) + 1 >= defaultStartMonth
            ? firstItem.year - 1
            : firstItem.year,
          getMonthForPoeplePage(firstItem.month) + 1,
        );
        setSelectedChartDimDate(dimDate);
      } else {
        setSelectedChartDimDate(defaultDimDate);
      }
    }
  }, []);

  const handleYear = useCallback(async (event) => {
    if (event.selectedYears.length === 0) {
      toast.warning('Please select the year!');
    }

    const _defaultdimdate = makeDimDate(
      event.selectedYears[0],
      defaultMonth,
      defaultStartMonth,
    );
    dispatch(PeopleActions.peopleSummaryRequest([_defaultdimdate]));
    dispatch(PeopleActions.peopleDetailRequest([_defaultdimdate]));
    handleFilter(event);
  }, [defaultMonth, defaultStartMonth, defaultDimDate, selectedCompany, dispatch, handleFilter]);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    setSelectedYears([defaultYear]);
    setLabel(defaultYear.toString());
    setSelectedMonths([financialMonth(defaultMonth, defaultStartMonth) - 1]);
    
    const savedTopItems = appdata.getLocalItem('peoSelectedTopItems');
    const initialTopItems = savedTopItems || [{
      year: defaultYear,
      month: financialMonth(defaultMonth, defaultStartMonth) - 1,
    }];
    setSelectedTopItems(initialTopItems);
    appdata.setLocalItem('peoSelectedTopItems', initialTopItems);
    setSelectedChartDimDate(appdata.getLocalItem('peoDimDate')?.[0] || defaultDimDate);

    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [defaultYear, defaultMonth, defaultStartMonth, onResize]);

  useEffect(() => {
    const savedDimDate = appdata.getLocalItem('peoDimDate') || [defaultDimDate];

    const missingDefaults = !lastFetchedCompany || !lastFetchedDimDate;
    if (missingDefaults) {
      // Clear any pending debounce
      dispatch(PeopleActions.peopleSummaryRequest(savedDimDate));
      dispatch(PeopleActions.peopleDetailRequest(savedDimDate));
      lastDispatchedRef.current = { company: selectedCompany, dimDate: defaultDimDate };
      return;
    }

    const valuesChanged =
      lastFetchedCompany !== selectedCompany ||
      lastFetchedDimDate !== defaultDimDate;

    // Only dispatch if values changed AND we haven't already dispatched for the new combination
    const alreadyDispatchedForNewValues = 
      lastDispatchedRef.current.company === selectedCompany &&
      lastDispatchedRef.current.dimDate === defaultDimDate;

    if (valuesChanged && !alreadyDispatchedForNewValues) {
      const stillNeedDispatch = 
        lastDispatchedRef.current.company !== selectedCompany ||
        lastDispatchedRef.current.dimDate !== defaultDimDate;

      if (stillNeedDispatch) {
        dispatch(PeopleActions.peopleSummaryRequest(savedDimDate));
        dispatch(PeopleActions.peopleDetailRequest(savedDimDate));
        lastDispatchedRef.current = { company: selectedCompany, dimDate: defaultDimDate };
      }
    }
  }, [defaultDimDate, selectedCompany, lastFetchedCompany, lastFetchedDimDate, dispatch]);

  return (
    <div style={{ height: '90vh' }}>
      <div className="wrapper" id="people_year_selector">
        <YearSelector
          selectedYears={selectedYears}
          label={label}
          onChange={handleYear}
        />
      </div>
      <div>
        <TopChart
          summaryData={summaryData}
          selectedYears={selectedYears}
          selectedMonths={selectedMonths}
          selectedTopItems={selectedTopItems}
          defaultMonth={defaultMonth}
          defaultStartMonth={defaultStartMonth}
          defaultYear={defaultYear}
          handleFilter={handleFilter}
          getPeopleDetail={(DimDate) => dispatch(PeopleActions.peopleDetailRequest(DimDate))}
        />
        <BottomChart
          detailData={detailData}
          selectedDimDate={selectedChartDimDate}
        />
      </div>

      {bubbleFlag && peopleSummary && (
        <div
          className="reporting-tooltip"
          style={{
            position: 'fixed',
            bottom: 60,
            right: 30,
            zIndex: 1000,
            opacity: 0.5,
            transition: 'opacity 0.3s ease',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => e.target.style.opacity = '1'}
          onMouseLeave={(e) => e.target.style.opacity = '0.5'}
        >
          <Tooltip 
            title={peopleSummary} 
            position="top-end"
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: '#2c3e50',
                  color: '#ffffff',
                  fontSize: '16px',
                  fontWeight: '500',
                  maxWidth: '400px',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
                }
              }
            }}
          >
            <InsertCommentIcon color="success" />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

People.propTypes = {
  defaultYear: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultDimDate: PropTypes.string.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  bubbleFlag: PropTypes.bool.isRequired,
};

export default People;
